
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface StripeSettingsProps {
  afterSubmit: {
    stripeEnabled?: boolean;
    stripePaymentLink?: string;
    stripeSuccessUrl?: string;
    stripeCancelUrl?: string;
  };
  onAfterSubmitChange: (settings: {
    stripeEnabled?: boolean;
    stripePaymentLink?: string;
    stripeSuccessUrl?: string;
    stripeCancelUrl?: string;
  }) => void;
}

export const StripeSettings = ({ afterSubmit, onAfterSubmitChange }: StripeSettingsProps) => {
  return (
    <Card className="p-6 mt-8 space-y-6 bg-white/50 backdrop-blur-sm border-slate-200/60">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-slate-800">Stripe Payment</h3>
          <p className="text-sm text-[#8E9196]">
            Set up the payment link that visitors will receive after form submission
          </p>
        </div>
        <Switch
          checked={afterSubmit?.stripeEnabled}
          onCheckedChange={(checked) => 
            onAfterSubmitChange({ ...afterSubmit, stripeEnabled: checked })
          }
        />
      </div>

      {afterSubmit?.stripeEnabled && (
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Important</AlertTitle>
            <AlertDescription>
              Make sure the return URLs set in your Stripe dashboard match the ones provided here.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="stripePaymentLink">Stripe Payment Link</Label>
            <Input
              id="stripePaymentLink"
              value={afterSubmit?.stripePaymentLink}
              onChange={(e) => 
                onAfterSubmitChange({ 
                  ...afterSubmit, 
                  stripePaymentLink: e.target.value 
                })
              }
              placeholder="https://buy.stripe.com/your-payment-link"
            />
            <p className="text-sm text-muted-foreground">
              Create a payment link in your Stripe dashboard and paste it here
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="stripeSuccessUrl">Success URL</Label>
            <Input
              id="stripeSuccessUrl"
              value={afterSubmit?.stripeSuccessUrl}
              onChange={(e) => 
                onAfterSubmitChange({ 
                  ...afterSubmit, 
                  stripeSuccessUrl: e.target.value 
                })
              }
              placeholder="https://your-domain.com/success"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="stripeCancelUrl">Cancel URL</Label>
            <Input
              id="stripeCancelUrl"
              value={afterSubmit?.stripeCancelUrl}
              onChange={(e) => 
                onAfterSubmitChange({ 
                  ...afterSubmit, 
                  stripeCancelUrl: e.target.value 
                })
              }
              placeholder="https://your-domain.com/cancel"
            />
          </div>
        </div>
      )}
    </Card>
  );
};
